import { useEffect, useState } from "react";
import axios from "axios";
import config from "./config";

/**
 * The strip under an embedded video: its title, its host, and a way out.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * A pasted video link renders as an inline player, and the URL is stripped from
 * the post text so it isn't repeated. That leaves the player with no context —
 * a bare 16:9 frame, no title, and no way to open the source. Facebook solves
 * the same problem with a link card, but a card costs a tap and sends the
 * member to YouTube, which is the wrong trade for a course community: the
 * reason a creator posts the video in the feed is so it is watched there.
 *
 * So: keep one-tap playback, and borrow the one thing the card does better.
 * The title comes from the same membership-link-preview endpoint the link card
 * already uses, so no new backend work.
 *
 * ── Degrades quietly ────────────────────────────────────────────────────────
 * A host that blocks scraping, a slow fetch, a video that has since been
 * deleted — all end with no title, and then only the host and the open link
 * are shown. Nothing here may ever prevent the video itself from rendering,
 * which is why the fetch is fire-and-forget and every failure is swallowed.
 */
export default function VideoCaption({ url }) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    setMeta(null);

    axios
      .post(`${config.COMMUNITY_API_URL}/membership-link-preview`, { url })
      .then((res) => {
        if (alive && res.data?.error === 0) setMeta(res.data);
      })
      .catch(() => {
        /* no title, just the host — see the note above */
      });

    return () => {
      alive = false;
    };
  }, [url]);

  let host = meta?.site || "";
  try {
    host = new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    /* keep whatever the preview reported */
  }

  return (
    <div className="flex items-center gap-3 rounded-b-xl border border-t-0 bg-gray-50 px-3 py-2">
      <div className="min-w-0 flex-1">
        {meta?.title ? (
          <p className="truncate text-[13.5px] font-semibold text-gray-900">{meta.title}</p>
        ) : null}
        <p className="truncate text-[11.5px] uppercase tracking-wide text-gray-400">{host}</p>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="shrink-0 text-[12.5px] font-semibold text-gray-500 hover:text-gray-800"
      >
        Open ↗
      </a>
    </div>
  );
}
